"use client";

import {
  useMutation,
  useQueryClient,
  type UseMutationResult,
} from "@tanstack/react-query";
import { createOrUpdateSocialProfile } from "../../client/manager/cross-posting";
import { invalidateCrossPosting } from "./invalidation";
import type { AyrshareProfileRow } from "../../models/ayrshare";

interface CreateOrUpdateSocialProfileInput {
  comedianId: string;
}

/**
 * Create (or return existing) Ayrshare profile for a comedian.
 * Invalidates scope: "profile" on success (D-05).
 *
 * Pessimistic per D-06 (no optimistic write). Returns raw useMutation
 * result per D-08 — consumer calls .mutate({ comedianId }) directly.
 */
export function useCreateOrUpdateSocialProfile(): UseMutationResult<
  AyrshareProfileRow,
  Error,
  CreateOrUpdateSocialProfileInput,
  unknown
> {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOrUpdateSocialProfileInput) =>
      createOrUpdateSocialProfile(input),
    onSuccess: async (_data, variables) => {
      await invalidateCrossPosting(queryClient, {
        comedianId: variables.comedianId,
        scope: "profile",
      });
    },
  });
}
